// src/services/coingecko.js
// Rate-limit friendly CoinGecko client with localStorage cache, SWR, and exponential backoff.
// Supports: markets (paged), trending (raw or hydrated markets), categories (sectors), coin details.

const BASE = "https://api.coingecko.com/api/v3";

/* ------------------------------- helpers -------------------------------- */

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const now = () => Date.now();

function readCache(key, maxAgeMs) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    if (obj.t + maxAgeMs < now()) return null; // stale
    return obj.v;
  } catch {
    return null;
  }
}

function writeCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify({ t: now(), v: value }));
  } catch {}
}

async function fetchWithBackoff(url, { signal, maxRetries = 3 } = {}) {
  let attempt = 0;
  while (true) {
    try {
      const res = await fetch(url, { headers: { accept: "application/json" }, signal });
      if (res.status === 429) {
        // Too many requests → exponential backoff
        const waitMs = Math.min(1000 * Math.pow(2, attempt), 10_000);
        await sleep(waitMs);
        attempt++;
        if (attempt > maxRetries) throw new Error("429 Too Many Requests");
        continue;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      if (err?.name === "AbortError") throw err;
      attempt++;
      if (attempt > maxRetries) throw err;
      await sleep(500 * attempt);
    }
  }
}

/**
 * SWR-ish fetch: return cached immediately (if exists), then refresh in background.
 * onRefresh(data) will be called when fresh data arrives.
 */
async function swrFetch(url, { cacheKey, maxAgeMs, onRefresh, signal, maxRetries } = {}) {
  const cached = readCache(cacheKey, maxAgeMs);
  // Refresh in background
  (async () => {
    try {
      const fresh = await fetchWithBackoff(url, { signal, maxRetries });
      writeCache(cacheKey, fresh);
      onRefresh && onRefresh(fresh);
    } catch {
      // keep cached
    }
  })();
  return cached;
}

/* ------------------------------- utilities ------------------------------- */

function qs(paramsObj) {
  const u = new URLSearchParams();
  Object.entries(paramsObj).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") u.set(k, String(v));
  });
  return u.toString();
}

function normVs(vs) {
  return String(vs || "usd").toLowerCase();
}

/* --------------------------------- API ---------------------------------- */

/**
 * getMarkets — simple page (defaults to 50)
 * Keeps backward compatibility with existing imports.
 */
export async function getMarkets(
  {
    vs = "usd",
    order = "market_cap_desc",
    perPage = 50,
    page = 1,
    changes = "1h,24h,7d",
    signal,
  } = {},
  { onRefresh } = {}
) {
  const params = {
    vs_currency: normVs(vs),
    order,
    per_page: perPage,
    page,
    price_change_percentage: changes,
    sparkline: false,
  };
  const url = `${BASE}/coins/markets?${qs(params)}`;
  const cacheKey = `cg:markets:${params.vs_currency}:${order}:${perPage}:${page}:${changes}`;
  // TTL 60s
  const cached = await swrFetch(url, {
    cacheKey,
    maxAgeMs: 60_000,
    onRefresh,
    signal,
    maxRetries: 3,
  });
  if (!cached) {
    const fresh = await fetchWithBackoff(url, { signal, maxRetries: 3 });
    writeCache(cacheKey, fresh);
    return fresh;
  }
  return cached;
}

/**
 * getMarketsPage — full page of up to 250 items, optional by category (sector).
 * Use this for “load all coins” pagination (250/page).
 */
export async function getMarketsPage(
  {
    vs = "usd",
    order = "market_cap_desc",
    perPage = 250,
    page = 1,
    changes = "1h,24h,7d",
    category, // optional CoinGecko category id (e.g., "layer-1", "smart-contract-platform")
    signal,
  } = {},
  { onRefresh } = {}
) {
  const params = {
    vs_currency: normVs(vs),
    order,
    per_page: Math.min(Number(perPage) || 250, 250),
    page,
    price_change_percentage: changes,
    sparkline: false,
    category,
  };
  const url = `${BASE}/coins/markets?${qs(params)}`;
  const cacheKey = `cg:marketsPage:${Object.values(params).join(":")}`;
  // TTL 60s
  const cached = await swrFetch(url, {
    cacheKey,
    maxAgeMs: 60_000,
    onRefresh,
    signal,
    maxRetries: 3,
  });
  if (!cached) {
    const fresh = await fetchWithBackoff(url, { signal, maxRetries: 3 });
    writeCache(cacheKey, fresh);
    return fresh;
  }
  return cached;
}

/**
 * getCategories — sectors overview (market caps by category).
 */
export async function getCategories(opts = {}, { onRefresh } = {}) {
  const url = `${BASE}/coins/categories`;
  const cacheKey = `cg:categories:v1`;
  // TTL 5 min
  const cached = await swrFetch(url, {
    cacheKey,
    maxAgeMs: 5 * 60_000,
    onRefresh,
    signal: opts?.signal,
    maxRetries: 3,
  });
  if (!cached) {
    const fresh = await fetchWithBackoff(url, { signal: opts?.signal, maxRetries: 3 });
    writeCache(cacheKey, fresh);
    return fresh;
  }
  return cached;
}

/**
 * getTrending — raw “/search/trending” JSON (coins[], exchanges[], …)
 * Recommended for UI widgets that show name/symbol/icon directly.
 */
export async function getTrending(opts = {}, { onRefresh } = {}) {
  const url = `${BASE}/search/trending`;
  const cacheKey = `cg:trending:raw`;
  // TTL 120s
  const cached = await swrFetch(url, {
    cacheKey,
    maxAgeMs: 120_000,
    onRefresh,
    signal: opts?.signal,
    maxRetries: 3,
  });
  if (!cached) {
    const fresh = await fetchWithBackoff(url, { signal: opts?.signal, maxRetries: 3 });
    writeCache(cacheKey, fresh);
    return fresh;
  }
  return cached;
}

/**
 * getTrendingMarkets — hydrate trending IDs into markets rows (price, 24h, …)
 * Useful if you want full market rows for just the trending set.
 */
export async function getTrendingMarkets(
  { vs = "usd", signal } = {},
  { onRefresh } = {}
) {
  const trending = await getTrending({ signal });
  const ids = (trending?.coins || []).map((c) => c?.item?.id).filter(Boolean);
  if (!ids.length) return [];

  const params = {
    vs_currency: normVs(vs),
    ids: ids.join(","),
    order: "market_cap_desc",
    per_page: 100,
    page: 1,
    price_change_percentage: "24h",
    sparkline: false,
  };
  const url = `${BASE}/coins/markets?${qs(params)}`;
  const cacheKey = `cg:trending:markets:${params.vs_currency}:${ids.join(",")}`;
  // TTL 120s
  const cached = await swrFetch(url, {
    cacheKey,
    maxAgeMs: 120_000,
    onRefresh,
    signal,
    maxRetries: 3,
  });
  if (!cached) {
    const fresh = await fetchWithBackoff(url, { signal, maxRetries: 3 });
    writeCache(cacheKey, fresh);
    return fresh;
  }
  return cached;
}

/**
 * getCoinDetails — rich details for side panel.
 */
export async function getCoinDetails(id, { signal } = {}, { onRefresh } = {}) {
  if (!id) return null;
  const url = `${BASE}/coins/${id}?localization=false&tickers=false&market_data=true&community_data=true&developer_data=true&sparkline=true`;
  const cacheKey = `cg:coin:${id}`;
  // TTL 180s
  const cached = await swrFetch(url, {
    cacheKey,
    maxAgeMs: 180_000,
    onRefresh,
    signal,
    maxRetries: 3,
  });
  if (!cached) {
    const fresh = await fetchWithBackoff(url, { signal, maxRetries: 3 });
    writeCache(cacheKey, fresh);
    return fresh;
  }
  return cached;
}
